import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { ENDPOINT } from '~/constants';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysFtpPoller } from './queryKeys';
import { FTP_PROTOCOL } from './useMutationCreateFtpPoller';

const zodFtpPollerFileCountPayload = z.object({
  password: z.string().min(1),
  path: z.string().min(1),
  port: z.number().min(1).max(65_535).optional().prefault(22),
  protocol: z.enum(FTP_PROTOCOL).optional().prefault('sftp'),
  server: z.string(), // TODO: validate; z.string().url()?
  username: z.string().min(1),
});

export type FtpPollerFileCountPayload = z.infer<
  typeof zodFtpPollerFileCountPayload
>;

type FtpPollerFileCountResponse = {
  errorCode: string;
  fileCount: number;
  path: string;
  success: boolean;
};

/**
 * Fetches the number of files in a directory on a FTP server.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/get_file_count_on_server_for_ftp_secret_vadmin_ftp_poller_secret_file_count_post
 */
export const fetchFtpPollerFileCount = async (
  ftpPollerFileCountPayload: FtpPollerFileCountPayload,
) => {
  try {
    const response = await vestigasApi
      .post(ENDPOINT.FTP_POLLER.GET_FILE_COUNT(), {
        json: ftpPollerFileCountPayload,
        validate: { body: zodFtpPollerFileCountPayload },
      })
      .json<FtpPollerFileCountResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error fetching FTP poller file count',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query based custom hook for getting the number of files in a directory on a FTP server.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/get_file_count_on_server_for_ftp_secret_vadmin_ftp_poller_secret_file_count_post
 */
export const useQueryFtpPollerFileCount = (
  {
    password,
    path,
    port,
    protocol,
    server,
    username,
  }: FtpPollerFileCountPayload,
  options?: Omit<
    UseQueryOptions<FtpPollerFileCountResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<FtpPollerFileCountResponse>({
    enabled: Boolean(server && username && password && path),
    queryFn: async () =>
      fetchFtpPollerFileCount({
        password,
        path,
        port,
        protocol,
        server,
        username,
      }),
    queryKey: queryKeysFtpPoller.getFileCount({
      password,
      path,
      port,
      protocol,
      server,
      username,
    }),
    ...options,
  });
};
